if (!customElements.get('similar-products')) {
  class SimilarProducts extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('product-card, .product-card') || this.parentElement;
      this.grid = this.querySelector('.similar-products__grid');
      this.swatches = Array.from(
        this.querySelectorAll('.similar-products__swatch-item:not(.similar-products__swatch-item--more)')
      );
      this.expanded = false;

      this.handleResize = () => {
        this.calculateLayout();
      };

      // Schedule layout calculations across initial renders
      requestAnimationFrame(() => this.calculateLayout());
      setTimeout(() => this.calculateLayout(), 100);
      setTimeout(() => this.calculateLayout(), 400);

      window.addEventListener('resize', this.handleResize);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.calculateLayout();
        });
        this.resizeObserver.observe(this);
        if (this.card) {
          this.resizeObserver.observe(this.card);
        }
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.expanded) {
              this.calculateLayout();
            }
          });
        }, { threshold: 0.1 });
        this.intersectionObserver.observe(this);
      }

      this.bindSwatchEvents();
    }

    bindSwatchEvents() {
      this.swatches = Array.from(
        this.querySelectorAll('.similar-products__swatch-item:not(.similar-products__swatch-item--more)')
      );
      this.swatches.forEach((thumb) => {
        if (thumb._eventsBound) return;
        thumb._eventsBound = true;

        thumb.addEventListener('mouseenter', () => {
          if (this.swatches.length <= 1) return;
          this.setActiveProduct(thumb);
        });

        const navigateToPdp = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const url = thumb.dataset.url;
          if (url && url !== '#') {
            window.location.href = url;
          }
        };

        thumb.addEventListener('click', navigateToPdp);
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            navigateToPdp(e);
          }
        });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }

    calculateLayout() {
      if (this.expanded) return;

      this.grid = this.querySelector('.similar-products__grid');
      if (!this.grid) return;

      this.swatches = Array.from(
        this.querySelectorAll('.similar-products__swatch-item:not(.similar-products__swatch-item--more)')
      );
      if (this.swatches.length === 0) return;

      const gridWidth = this.grid ? (this.grid.clientWidth || this.grid.getBoundingClientRect().width) : 0;
      const containerWidth = Math.floor(
        gridWidth ||
        this.clientWidth ||
        this.getBoundingClientRect().width ||
        (this.card ? this.card.clientWidth : 0) ||
        (this.card ? this.card.getBoundingClientRect().width : 0) ||
        0
      );

      if (containerWidth <= 0) return;

      const existingMore = this.querySelector('.similar-products__swatch-item--more');
      if (existingMore) existingMore.remove();

      // Reset visibility for measurement
      this.swatches.forEach((s) => s.classList.remove('similar-products__swatch-item--hidden'));

      const firstSwatch = this.swatches[0];
      const defaultSwatchSize = window.innerWidth < 750 ? 32 : parseInt(this.dataset.size) || 44;
      const itemWidth = firstSwatch && firstSwatch.offsetWidth > 0 ? firstSwatch.offsetWidth : defaultSwatchSize;
      const gridComputed = window.getComputedStyle(this.grid);
      const gap = parseFloat(gridComputed.gap) || 4;

      const totalItemWidthWithGap = itemWidth + gap;

      const isMobile = window.innerWidth < 750;
      const maxVisibleSwatches = isMobile ? 4 : 5; // Show max swatches in a single row

      // Check if all swatches fit without needing a "+ More" button
      const totalWidthAllSwatches = this.swatches.length * itemWidth + (this.swatches.length - 1) * gap;

      let fitCount;
      if (totalWidthAllSwatches <= containerWidth && this.swatches.length <= maxVisibleSwatches) {
        fitCount = this.swatches.length;
      } else {
        // Reserve itemWidth space for "+ More" button and fit remaining swatches
        fitCount = Math.floor((containerWidth - itemWidth) / totalItemWidthWithGap);
        if (fitCount > maxVisibleSwatches) {
          fitCount = maxVisibleSwatches;
        }
      }

      if (fitCount < 1) fitCount = 1;
      if (fitCount >= this.swatches.length) fitCount = this.swatches.length;

      const hiddenCount = this.swatches.length - fitCount;
      if (hiddenCount > 0) {
        for (let i = fitCount; i < this.swatches.length; i++) {
          this.swatches[i].classList.add('similar-products__swatch-item--hidden');
        }

        const moreBtn = document.createElement('button');
        moreBtn.type = 'button';
        moreBtn.className = 'similar-products__swatch-item similar-products__swatch-item--more';
        moreBtn.innerHTML = `<span>+${hiddenCount}<br/>More</span>`;
        moreBtn.setAttribute('aria-label', `View ${hiddenCount} more products`);
        moreBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const activeSwatch = this.querySelector('.similar-products__swatch-item.selected') || this.swatches[0];
          const url = moreBtn.dataset.url || activeSwatch?.dataset.url || this.dataset.productUrl;
          if (url && url !== '#') {
            window.location.href = url;
          }
        });

        this.grid.appendChild(moreBtn);
      }

      this.bindSwatchEvents();
    }

    revealAll() {
      this.expanded = true;
      if (this.grid) {
        this.grid.classList.add('expanded');
        this.grid.style.overflow = 'visible';
      }
      this.style.overflow = 'visible';
      this.swatches.forEach((s) => {
        s.classList.remove('similar-products__swatch-item--hidden');
      });
      const moreBtn = this.querySelector('.similar-products__swatch-item--more');
      if (moreBtn) {
        moreBtn.remove();
      }
      window.removeEventListener('resize', this.handleResize);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }
    }

    setActiveProduct(thumb) {
      if (!thumb || this.swatches.length <= 1) return;

      const handle = thumb.dataset.handle;
      const title = thumb.dataset.title;
      const priceHtml = thumb.querySelector('.similar-products__price-data')?.innerHTML;
      const productUrl = thumb.dataset.url;

      // 1. Highlight active thumbnail
      this.thumbnails = this.querySelectorAll(
        '.similar-products__swatch-item:not(.similar-products__swatch-item--more)'
      );
      this.thumbnails.forEach((t) => t.classList.toggle('selected', t === thumb));

      // Update + More button url if present
      const moreBtn = this.querySelector('.similar-products__swatch-item--more');
      if (moreBtn && productUrl) {
        moreBtn.href = productUrl;
        moreBtn.dataset.url = productUrl;
      }

      // 2. Update card link URLs
      const links = this.card.querySelectorAll(
        'a.product-card__link, [ref="productCardLink"], [ref="productTitleLink"], [ref="cardGalleryLink"]'
      );
      links.forEach((link) => {
        link.href = productUrl;
      });

      // 3. Update title
      const titleContainer = this.card.querySelector(
        'product-title, .product-card__title, [ref="productTitleLink"] p'
      );
      if (titleContainer) {
        titleContainer.textContent = title;
      }

      // 4. Update price
      const priceContainer = Array.from(this.card.querySelectorAll('product-price, [ref="priceContainer"]'))
        .find(el => !el.closest('similar-products'));
      if (priceContainer && priceHtml) {
        priceContainer.innerHTML = priceHtml;
      }

      // 5. Update main image layer
      const gallery = this.card.querySelector('.card-gallery');
      let layer;
      if (gallery) {
        layer = gallery.querySelector('.similar-products__image-layer');
        if (!layer) {
          layer = document.createElement('div');
          layer.className = 'similar-products__image-layer';
          layer.setAttribute('aria-hidden', 'true');

          const templatesContainer = this.querySelector('.similar-products__templates');
          if (templatesContainer) {
            layer.innerHTML = templatesContainer.innerHTML;
          }
          gallery.appendChild(layer);
          gallery.classList.add('card-gallery--has-similar-products', 'card-gallery--similar-overlay-ready');
        }

        const colourways = layer.querySelectorAll('.similar-products__colourway');
        colourways.forEach((c) => {
          c.classList.toggle('selected', c.dataset.handle === handle);
        });
      }

      // 6. Update Quick Add component size list
      const quickAdd = this.card.querySelector('quick-add-component');
      if (quickAdd) {
        let variantsContainer;
        if (layer) {
          const colourway = layer.querySelector(`.similar-products__colourway[data-handle="${handle}"]`);
          if (colourway) {
            variantsContainer = colourway.querySelector('.similar-products__variants-data');
          }
        }
        if (!variantsContainer) {
          const templatesContainer = this.querySelector('.similar-products__templates');
          const colourway = templatesContainer?.querySelector(
            `.similar-products__colourway[data-handle="${handle}"]`
          );
          if (colourway) {
            variantsContainer = colourway.querySelector('.similar-products__variants-data');
          }
        }

        const variantCount = variantsContainer ? variantsContainer.children.length : 0;

        if (typeof quickAdd.closeSizesOverlay === 'function') {
          quickAdd.closeSizesOverlay();
        }

        if (variantCount === 1 && variantsContainer) {
          quickAdd.setAttribute('data-quick-add-button', 'add');
          const singleVariantId = variantsContainer.firstElementChild.dataset.variantId;
          const form = quickAdd.querySelector('form');
          const variantInput = form?.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.value = singleVariantId;
            variantInput.removeAttribute('disabled');
          }
        } else if (variantsContainer) {
          quickAdd.setAttribute('data-quick-add-button', 'choose');

          if (quickAdd.sizesGrid) {
            quickAdd.sizesGrid.innerHTML = variantsContainer.innerHTML;
            if (typeof quickAdd.setupSizeButtonListeners === 'function') {
              quickAdd.setupSizeButtonListeners();
            }
          } else {
            let overlay = quickAdd.querySelector('.quick-add-sizes-overlay');
            if (!overlay) {
              overlay = document.createElement('div');
              overlay.className = 'quick-add-sizes-overlay';
              overlay.setAttribute('ref', 'sizesOverlay');

              const grid = document.createElement('div');
              grid.className = 'quick-add-sizes-grid';
              grid.setAttribute('ref', 'sizesGrid');

              overlay.appendChild(grid);
              quickAdd.appendChild(overlay);

              quickAdd.sizesOverlay = overlay;
              quickAdd.sizesGrid = grid;
            }

            quickAdd.sizesGrid.innerHTML = variantsContainer.innerHTML;
            if (typeof quickAdd.setupSizeButtonListeners === 'function') {
              quickAdd.setupSizeButtonListeners();
            }
          }
        }
      }
    }
  }
  customElements.define('similar-products', SimilarProducts);
}
